import { useMemo } from 'react'

import { useSelector } from 'react-redux'

import type { PortionSizes } from 'config/portionSizes'
import { useRecipes } from 'hooks/menu-recipes/useRecipes'
import { useStaticArray } from 'hooks/useStaticArray'
import type { Recipe, RecipeUUID } from 'reducers/menu-recipes/recipes'
import { getBasketRecipes, getNumPortions } from 'selectors/basket'
import { getBasketRecipeNumPortions, getBasketRecipeQuantity } from 'utils/basket'

export type BasketRecipe = {
  id: RecipeUUID
  recipe: Recipe | null // null means recipe is not loaded
  quantity: number
  numPortions: PortionSizes
}

/**
 * Returns an array of basket recipes with their core IDs and the corresponding recipe objects.
 */
export const useBasketRecipes = () => {
  const basketRecipeEntries = useSelector(getBasketRecipes)
  const numPortions = useSelector(getNumPortions)
  const basketUUIDs = useMemo(() => Array.from(basketRecipeEntries.keys()), [basketRecipeEntries])
  const recipes = useRecipes(basketUUIDs)
  const staticRecipes = useStaticArray(recipes)

  const basketRecipes = useMemo(
    () =>
      basketUUIDs.map((id, index) => ({
        id,
        recipe: staticRecipes[index] || null,
        quantity: getBasketRecipeQuantity(basketRecipeEntries.get(id)),
        numPortions: getBasketRecipeNumPortions(basketRecipeEntries.get(id), numPortions),
      })),
    [basketUUIDs, staticRecipes, basketRecipeEntries, numPortions],
  )

  const basketIdsSet = new Set(
    basketRecipes.filter((recipe) => recipe.quantity > 0).map((recipe) => recipe.id),
  )

  const basketHasRecipe = (id: RecipeUUID) => basketIdsSet.has(id)

  return {
    basketRecipes,
    basketHasRecipe,
  }
}
